import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, Legend } from "recharts";

const BAND_COLORS = { low: "#16A34A", medium: "#EAB308", high: "#F97316", critical: "#DC2626" };

export default function Analytics() {
  const [sum, setSum] = useState(null);
  useEffect(() => { api.get("/analytics/summary").then(r => setSum(r.data)); }, []);
  if (!sum) return <div className="p-8">Loading…</div>;

  const bandData = Object.entries(sum.by_band).map(([k,v]) => ({ name: k, value: v, color: BAND_COLORS[k] }));
  const catData = Object.entries(sum.by_category).map(([k,v]) => ({ name: k.replace("_"," "), value: v }));
  const statusData = Object.entries(sum.by_status).map(([k,v]) => ({ name: k.replace("_"," "), value: v }));

  return (
    <div className="p-8 md:p-10 max-w-[1600px] mx-auto">
      <div className="overline">Programmatic Insight</div>
      <h1 className="font-heading font-extrabold text-4xl sm:text-5xl tracking-tight mt-2 mb-8">Analytics</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 border border-slate-200">
        <div className="p-6 lg:border-r border-slate-200 border-b lg:border-b-0">
          <div className="overline mb-4">Intake trend (7d)</div>
          <div className="h-72">
            <ResponsiveContainer><AreaChart data={sum.trend}>
              <defs><linearGradient id="grad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0033A0" stopOpacity={0.4}/><stop offset="100%" stopColor="#0033A0" stopOpacity={0}/></linearGradient></defs>
              <XAxis dataKey="date" tick={{fontSize:11}} tickFormatter={d => d.slice(5)} />
              <YAxis allowDecimals={false} tick={{fontSize:11}} />
              <Tooltip />
              <Area type="monotone" dataKey="count" stroke="#0033A0" fill="url(#grad)" strokeWidth={2} />
            </AreaChart></ResponsiveContainer>
          </div>
        </div>
        <div className="p-6">
          <div className="overline mb-4">Risk Distribution</div>
          <div className="h-72">
            <ResponsiveContainer><PieChart>
              <Pie data={bandData} dataKey="value" nameKey="name" outerRadius={90} label>
                {bandData.map((e,i) => <Cell key={i} fill={e.color} />)}
              </Pie>
              <Legend />
            </PieChart></ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 border border-t-0 border-slate-200">
        <div className="p-6 lg:border-r border-slate-200 border-b lg:border-b-0">
          <div className="overline mb-4">Status Breakdown</div>
          <div className="space-y-3 mt-2">
            {statusData.map(s => {
              const max = Math.max(...statusData.map(d => d.value), 1);
              return (
                <div key={s.name} className="grid grid-cols-[140px_1fr_50px] items-center gap-4 text-sm">
                  <div className="capitalize text-slate-600">{s.name}</div>
                  <div className="h-6 bg-slate-100"><div className="h-full bg-slate-900" style={{width: `${(s.value/max)*100}%`}} /></div>
                  <div className="font-mono text-right">{s.value}</div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="p-6">
          <div className="overline mb-4">Category Breakdown</div>
          <div className="space-y-3 mt-2">
            {catData.map(c => {
              const max = Math.max(...catData.map(d => d.value), 1);
              return (
                <div key={c.name} className="grid grid-cols-[140px_1fr_50px] items-center gap-4 text-sm">
                  <div className="capitalize text-slate-600">{c.name}</div>
                  <div className="h-6 bg-slate-100"><div className="h-full bg-[#0033A0]" style={{width: `${(c.value/max)*100}%`}} /></div>
                  <div className="font-mono text-right">{c.value}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
